import { Component, OnInit, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { MatSidenav } from '@angular/material/sidenav';
import { ActivatedRoute, Router } from '@angular/router';
import { DialogMarkAsPaidComponent } from '@app/modules/statements/components/dialog-mark-as-paid/dialog-mark-as-paid.component';
import { DialogPublishComponent } from '@app/modules/statements/components/dialog-publish/dialog-publish.component';
import { DialogUnpublishComponent } from '@app/modules/statements/components/dialog-unpublish/dialog-unpublish.component';
import {
  Adjustment,
  BrandSettingsCustomizationOptions,
  StatementDetail,
  StatementDetailAction,
  StatementType,
} from '@app/modules/businesses/models/statements.models';
import { UpsellService } from '@app/shared/+state/shared/upsell.service';
import { BehaviorSubject, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, filter, map, switchMap, take, tap } from 'rxjs/operators';
import { StatementsFacade } from '../../../businesses/services/statements.facade';

@Component({
  standalone: false,
  selector: 'hospitable-statements-statement-detail',
  styleUrls: ['./statements-statement-detail.component.scss'],
  templateUrl: './statements-statement-detail.component.html',
})
export class StatementsStatementDetailComponent implements OnInit {
  @ViewChild('previewPane') previewPane!: MatSidenav;
  public loading$ = new BehaviorSubject<boolean>(false);
  public statement$ = new BehaviorSubject<StatementDetail>(null);
  public downloading$ = new BehaviorSubject<boolean>(false);
  public downloadingPreview$ = new BehaviorSubject<boolean>(false);
  public publishing$ = new BehaviorSubject<boolean>(false);
  public unpublishing$ = new BehaviorSubject<boolean>(false);
  public paying$ = new BehaviorSubject<boolean>(false);
  public previewedAdjustment$ = this.route.queryParams.pipe(
    map((params) => params['adjustmentUuid']),
    distinctUntilChanged(),
    switchMap((adjustmentUuid) =>
      this.statementsService.adjustmentsState$.pipe(
        filter((state) => !!state.data),
        map((state) => state.data.find((a) => a.uuid === adjustmentUuid) ?? null)
      )
    )
  );

  public adjustmentsState$ = this.statementsService.adjustmentsState$;
  public cannotEditNote$ = this.statement$.pipe(
    map((statement: StatementDetail) => {
      return statement.actions.find((action: StatementDetailAction) => action.type === 'add_note')?.description;
    })
  );

  public saveStatus$ = new BehaviorSubject<number>(0);
  public note = new FormControl<string>('', { updateOn: 'change' });
  protected BrandSettingsCustomizationOptions = BrandSettingsCustomizationOptions;

  constructor(
    private statementsService: StatementsFacade,
    private route: ActivatedRoute,
    private dialog: MatDialog,
    private upsellService: UpsellService,
    private router: Router
  ) {}

  public ngOnInit() {
    this.getStatementDetail();
    this.statement$
      .pipe(
        filter((statement) => !!statement),
        take(1)
      )
      .subscribe((statement) => {
        this.statementsService.setAdjustments(statement.adjustments, {
          current_page: 1,
          from: 1,
          last_page: 1,
          per_page: statement.adjustments.length,
          to: 10,
          total: statement.adjustments.length,
          links: [],
          path: '',
        });
      });

    // Listen for changes to the note and save it
    this.note.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        tap(() => this.saveStatus$.next(0)),
        // Use switchMap to cancel previous requests
        switchMap((note) => {
          return this.statementsService.updateStatementNote(this.statementId, note).pipe(
            // Handle errors per request
            catchError((error) => {
              this.saveStatus$.next(-1);
              return of(null);
            })
          );
        })
      )
      .subscribe({
        next: (updatedStatement) => {
          if (!updatedStatement) return;

          this.statement$.next(updatedStatement);
          this.saveStatus$.next(2);
          setTimeout(() => {
            this.saveStatus$.next(0);
          }, 3000);
        },
      });
  }

  public downloadStatement() {
    this.downloading$.next(true);
    this.statementsService.downloadStatementPdf(this.statementId).subscribe(
      (response) => {
        this.downloading$.next(false);
        this.statementsService.openMediaInNewTab(response);
      },
      (error) => {
        this.downloading$.next(false);
        console.error('Error opening PDF:', error);
      }
    );
  }

  public downloadPreview() {
    this.downloadingPreview$.next(true);
    this.statementsService.downloadStatementPreview(this.statementId).subscribe(
      (response) => {
        this.downloadingPreview$.next(false);
        this.statementsService.openMediaInNewTab(response);
      },
      (error) => {
        this.downloadingPreview$.next(false);
        console.error('Error opening PDF:', error);
      }
    );
  }

  public publishStatement() {
    this.publishing$.next(true);
    this.dialog
      .open(DialogPublishComponent, {
        maxWidth: '720px',
        width: '90vw',
        data: {
          statementId: this.statementId,
        },
      })
      .afterClosed()
      .pipe(
        switchMap((result) =>
          result === 'publish' ? this.statementsService.publishStatement(this.statementId) : of(result)
        ),
        tap(() => {
          this.publishing$.next(false);
        })
      )
      .subscribe(
        (result) => {
          // Limited to primary users
          if (result === 'upgrade') {
            this.upsellService.contactSalesForMogul('StatementsPublishDialog');
            return;
          }

          if (result === 'connect-quickbooks') {
            this.router.navigate(['/operations/statements/accounting']);
            return;
          }

          this.getStatementDetail();
        },
        () => {
          this.publishing$.next(false);
        }
      );
  }

  public unpublishStatement(type: StatementType) {
    this.unpublishing$.next(true);
    this.dialog
      .open(DialogUnpublishComponent, {
        maxWidth: '720px',
        width: '90vw',
        data: { type: type },
      })
      .afterClosed()
      .pipe(
        switchMap((result) =>
          result === 'unpublish' ? this.statementsService.unpublishStatement(this.statementId) : of(result)
        ),
        tap(() => {
          this.unpublishing$.next(false);
        })
      )
      .subscribe(
        () => {
          this.getStatementDetail();
        },
        () => {
          this.unpublishing$.next(false);
        }
      );
  }
  public markAsPaid(type: StatementType) {
    const statement = this.statement$.getValue();
    const summaryItem = statement.summary.find(
      (item) => item.title === 'Total due to owner' || item.title === 'Total due to manager'
    );

    this.paying$.next(true);
    this.dialog
      .open(DialogMarkAsPaidComponent, {
        maxWidth: '720px',
        width: '90vw',
        data: { type: type, ownerName: statement.owner.name, amountOwed: summaryItem.value },
      })
      .afterClosed()
      .pipe(
        switchMap((result) => {
          if (result && typeof result === 'string') {
            return this.statementsService.markStatementAsPaidWithDate(this.statementId, result);
          }
          return of(result);
        }),
        tap(() => {
          this.paying$.next(false);
        })
      )
      .subscribe(
        () => {
          this.getStatementDetail();
        },
        () => {
          this.paying$.next(false);
        }
      );
  }

  public downloadAttachment(attachmentId: string) {
    this.statementsService.downloadAttachment(attachmentId).subscribe(
      (response) => {
        this.statementsService.openMediaInNewTab(response);
      },
      (error) => {
        console.error('Error downloading attachment:', error);
      }
    );
  }

  public onAdjustmentSelected(adjustmentUuid: Adjustment['uuid'] | null): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { adjustmentUuid: adjustmentUuid || null },
      queryParamsHandling: 'merge',
    });
  }

  public onAdjustmentApplied(): void {
    this.getStatementDetail();
  }

  public openCustomizeDrawer(): void {
    this.previewPane.open();
  }

  private getStatementDetail() {
    this.loading$.next(true);
    this.route.paramMap
      .pipe(
        switchMap(() =>
          this.route.queryParams.pipe(
            switchMap((queryParams) => this.statementsService.getStatementDetail(this.statementId, queryParams))
          )
        ),
        tap((statement) => {
          this.loading$.next(false);
          this.statement$.next(statement);

          if (!statement.actions.find((action: StatementDetailAction) => action.type === 'add_note')?.enabled) {
            // Don't want to trigger a value change event which will save the note
            this.note.disable({ emitEvent: false });
          }
          if (!this.note.dirty && Boolean(statement.note)) {
            // Don't want to trigger a value change event which will save the note
            this.note.setValue(statement.note, { emitEvent: false });
          }
        }),
        take(1)
      )
      .subscribe((statement) => {
        this.statement$.next(statement);
      });
  }

  public get statementId() {
    return this.route.snapshot.params['id'];
  }

  public closePreviewPane() {
    this.previewPane.close();
    this.getStatementDetail();
  }
}
